<template>

  <div class="login">
    <div class="container">
      <div class="form-wrapper">
        <div class="row">
          <div class="col-md-4">
            <div class="login-left">
              <img src="@/assets/logo.png" alt=""/>
              <h3 class="mt-2">Welcome</h3>
              <p>to the Artisio platform</p>
            </div>
          </div>
          <div class="col-md-8 col-right">
            <div class="login-right clearfix">
              <h3 class="login-heading mb-0">{{$t('Artisio Account Login')}}</h3>
              <b-alert class="mb-0 mt-3 login-alert-msg-box" variant="danger" :show="loginAlertMsg" dismissible>
                {{ loginAlertMsg }}
              </b-alert>
              <br>
              <!-- SSO Button -->
              <div v-if="ssoConfig.enabled" class="mb-3">
                <b-button
                  variant="outline-primary"
                  block
                  @click="onSsoButtonClick"
                  :disabled="ssoLoading"
                  class="sso-button"
                >
                  <span v-if="!ssoLoading" class="d-flex align-items-center justify-content-center">
                    <img src="@/assets/Microsoft_Entra_ID.png" class="mr-2" width="24"/>
                    {{ $t('Sign in with Entra ID') }}
                  </span>
                  <span v-else>{{ $t('Loading...') }}</span>
                </b-button>
                <div class="text-center mt-2 mb-2">
                  <span class="text-muted">{{ $t('or') }}</span>
                </div>
              </div>
              <form v-on:submit.prevent="onEmailPasswordFormSubmit"
                    v-show="showEmailPasswordForm">
                <ValidationObserver ref="emailPasswordForm" v-slot="{invalid, errors}">
                  <div class="login-form">
                    <input-widget ref="emailInputWidget" :model="emailPasswordFormModel" attribute="email"/>
                    <input-widget :model="emailPasswordFormModel" attribute="password" type="password"/>
                    <div ref="turnstileBox" id="turnstile-box" data-theme="light"></div>
                    <b-form-invalid-feedback :state="!emailPasswordFormModel.hasError('captcha')">
                      {{ emailPasswordFormModel.getFirstError('captcha') }}
                    </b-form-invalid-feedback>

                    <div v-if="isProduction && captchaErrorMessage" class="text-danger mt-1" style="font-size: 80%">
                      {{ captchaErrorMessage }}
                    </div>
                    <div class="d-flex align-items-center justify-content-between mt-2">
                      <submit-button :disabled="emailPasswordLoading || invalid"
                                     :text="$t('Log In')"
                                     :show-loader="emailPasswordLoading"/>
                    </div>
                  </div>
                </ValidationObserver>
              </form>
              <form v-on:submit.prevent="onCodeFormSubmit" v-show="!showEmailPasswordForm">
                <ValidationObserver ref="codeForm" v-slot="{invalid, errors}">
                  <div class="login-form code-form">
                    <b-form-group class="d-inline-block mb-4 ml-auto" v-if="!hideAuthenticatorChoice">
                      <b-form-radio-group
                        id="btn-radios-3"
                        size="sm"
                        v-model="codeFormModel.type"
                        :options="codeTypeOptions"
                        buttons
                        button-variant="outline-primary"
                        name="radio-btn-stacked"></b-form-radio-group>
                    </b-form-group>
                    <input-widget ref="codeInputWidget"
                                  :model="codeFormModel"
                                  attribute="code"
                                  class="mb-1"
                                  :label="codeFormModel.type === 'email' ? $t('Email Code') : $t('Authenticator Code')"
                                  type="number"/>

                    <b-button variant="link" class="p-0 mb-3" @click="resendCode" v-if="codeFormModel.type === 'email'">
                      {{ emailCodeWasRequested ? $t("Didn't receive? Click to resend") : $t('Request Email Code') }}
                    </b-button>

                    <input-widget :model="codeFormModel" attribute="remember_device" type="checkbox" is-switch class="mb-2"/>

                    <div class="d-flex align-items-center justify-content-between">
                      <submit-button :disabled="codeLoading || invalid"
                                     :text="$t('Confirm')"
                                     :show-loader="codeLoading"/>
                      <b-button variant="secondary" @click.prevent="goBackClick()">
                        {{ $t('Go Back') }}
                      </b-button>
                    </div>
                  </div>
                </ValidationObserver>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

  import auth from '../../../core/services/auth.service';
  import ssoService from '../../../core/services/sso.service';
  import InputWidget from "../../../core/components/input-widget/InputWidget";
  import EmailPasswordForm from "./EmailPasswordForm.model";
  import http from "../../../core/services/http.service";
  import {AppSettings} from "@/shared/AppSettings";
  import CodeForm from "./CodeForm.model";
  import SubmitButton from "../../../core/components/common/SubmitButton";
  import {createNamespacedHelpers} from "vuex";
  import {sha256} from "@/plugins/hash";
  import i18n from "@/shared/i18n";

  const {mapActions, mapState} = createNamespacedHelpers('global');

  export default {
    name: "Login",
    components: {SubmitButton, InputWidget},
    data: function () {
      return {
        showEmailPasswordForm: true,
        emailPasswordFormModel: new EmailPasswordForm(),
        emailPasswordLoading: false,
        codeFormModel: new CodeForm(),
        codeLoading: false,
        captchaErrorMessage: null,
        isProduction: AppSettings.isProduction(),
        emailCodeWasRequested: false,
        hideAuthenticatorChoice: false,
        ssoConfig: {enabled: false},
        ssoLoading: false,
        codeTypeOptions: [
          {text: i18n.t('Authenticator App'), value: 'authenticator', disabled: false},
          {text: i18n.t('Email'), value: 'email', disabled: false},
        ]
      }
    },
    computed: {
      ...mapState(['loginAlertMsg']),
    },
    methods: {
      ...mapActions(['updateWarning', 'updateLoginAlertMessage']),
      goBackClick() {
        this.emailPasswordFormModel = new EmailPasswordForm()
        this.codeFormModel = new CodeForm()
        this.captchaErrorMessage = null;
        this.showEmailPasswordForm = true;
        this.hideAuthenticatorChoice = false;
        this.emailCodeWasRequested = false;
        this.emailPasswordFormModel.resetErrors();
        this.$refs.emailPasswordForm.reset();
      },
      verifyTurnstile(verified, response) {
        if (!verified) {
          this.captchaErrorMessage = this.$i18n.t('Please, verify Captcha');
          return;
        }
        this.emailPasswordFormModel.captcha = response;
      },
      async onEmailPasswordFormSubmit() {
        this.updateLoginAlertMessage(null)

        if (this.isProduction && !this.emailPasswordFormModel.captcha) {
          this.captchaErrorMessage = this.$i18n.t('Please, verify captcha');
          return true;
        }

        this.emailPasswordFormModel.resetErrors();
        this.$refs.codeForm.reset();

        this.emailPasswordLoading = true;
        const hashedEmail = await sha256(this.emailPasswordFormModel.email.trim().toLowerCase());

        let {body, success} = await http.post(AppSettings.getUrl('/api/v1/users/auth/'), {
          email: this.emailPasswordFormModel.email,
          password: this.emailPasswordFormModel.password,
          captcha: this.emailPasswordFormModel.captcha,
          device_token: auth.getRememberDevice(hashedEmail)
        });
        this.emailPasswordLoading = false;
        window.turnstile.reset("#turnstile-box");
        this.emailPasswordFormModel.captcha = null;
        if (success) {
          if (body.token) {
            auth.setToken(body.token);
            if (body.warning) {
              this.updateWarning(body.warning);
            }
            this.$router.push('/');
            window.turnstile.remove();
          } else if (body.unique_code) {
            this.showEmailPasswordForm = false;
            this.codeFormModel.unique_code = body.unique_code;
            this.codeFormModel.type = body.authenticator ? 'authenticator' :'email';
            this.hideAuthenticatorChoice = !body.authenticator;
            this.emailCodeWasRequested = !body.authenticator;
            setTimeout(() => this.$refs.codeInputWidget.focus(), 200);
            this.$refs.codeForm.reset()
          }
        } else {
          this.emailPasswordFormModel.setMultipleErrors(body);
          if (body.non_field_errors) {
            this.emailPasswordFormModel.setErrors('email', body.non_field_errors)
          }
          if (body.detail) {
            this.emailPasswordFormModel.setErrors('email', [body.detail])
          }
        }
      },
      async onCodeFormSubmit() {
        this.codeFormModel.resetErrors();
        this.$refs.emailPasswordForm.reset();

        this.codeLoading = true;
        let {body, success} = await http.post(AppSettings.getUrl('/api/v1/users/auth-by-code/'), {
          code: this.codeFormModel.code,
          unique_code: this.codeFormModel.unique_code,
          type: this.codeFormModel.type,
          remember_device: this.codeFormModel.remember_device
        });
        this.codeLoading = false;

        if (success) {
          auth.setToken(body.token);
          if (body.warning) {
            this.updateWarning(body.warning);
          }
          if (body.remember_device) {
            const hashedEmail = await sha256(this.emailPasswordFormModel.email.trim().toLowerCase());
            auth.setRememberDevice(hashedEmail, body.remember_device);
          }
          this.$router.push('/');
          window.turnstile.remove();
        } else {
          this.codeFormModel.setMultipleErrors(body);
          if (body.non_field_errors) {
            this.codeFormModel.setErrors('code', body.non_field_errors)
          }
          if (body.detail) {
            this.codeFormModel.setErrors('code', [body.detail])
          }
        }
      },
      async resendCode() {
        let {body, success} = await http.post(AppSettings.getUrl('/api/v1/users/auth-resend-code/'), {
          unique_code: this.codeFormModel.unique_code
        });
        if (success) {
          this.emailCodeWasRequested = true;
          this.$toast('success', this.$i18n.t('Code was successfully sent'));
          this.codeFormModel.unique_code = body.unique_code;
        } else {
          if (body.unique_code) {
            this.codeFormModel.setErrors('code', [body.unique_code])
          }
        }
      },
      async onSsoButtonClick() {
        if (!this.ssoConfig.enabled) {
          return;
        }
        this.ssoLoading = true;
        try {
          ssoService.initiateSso(this.ssoConfig);
        } catch (error) {
          this.ssoLoading = false;
          this.updateLoginAlertMessage(error.message || this.$i18n.t('SSO initialization failed'));
        }
      },
      async checkSsoCallback() {
        // Only check for SSO callback if we're on the callback route
        if (this.$route.path !== '/auth/sso/callback') {
          return;
        }
        
        try {
          const result = await ssoService.handleCallback();
          
          if (result.success && result.token) {
            auth.setToken(result.token);
            // Clean up URL
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            url.searchParams.delete('error');
            url.searchParams.delete('error_description');
            window.history.replaceState({}, document.title, url.pathname);
            // Redirect to dashboard
            this.$router.push('/');
          } else {
            // Show error and redirect to login
            this.updateLoginAlertMessage(result.error || this.$i18n.t('SSO authentication failed'));
            this.$router.push('/login');
          }
        } catch (error) {
          this.updateLoginAlertMessage(error.message || this.$i18n.t('SSO authentication failed'));
          this.$router.push('/login');
        }
      },
      async fetchSsoConfig() {
        try {
          const domain = window.location.host;
          console.log('Fetching SSO config for domain:', domain);
          this.ssoConfig = await ssoService.getSsoConfig();
          console.log('SSO config result:', this.ssoConfig);
        } catch (error) {
          console.error('Failed to fetch SSO config:', error);
          this.ssoConfig = {enabled: false};
        }
      }
    },
    async mounted() {
      // Fetch SSO config
      await this.fetchSsoConfig();
      
      setTimeout(() => {
        if (this.$refs.emailInputWidget) {
          this.$refs.emailInputWidget.focus();
        }
      }, 500);

      const turnstileSiteKey = AppSettings.getTurnstileSiteKey();

      if (turnstileSiteKey && turnstileSiteKey !== '') {
        function setTurnstileListeners() {
          window.turnstile.render("#turnstile-box", {
            sitekey: turnstileSiteKey,
            callback: (response) => this.verifyTurnstile(true, response),
            'expired-callback': this.verifyTurnstile(false, 'expired'),
            'error-callback': this.verifyTurnstile(false, 'failed')
          })
        }

        if (window.turnstile === null || !window.turnstile) {
          const script = document.createElement('script')
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback'
          script.async = true
          script.defer = true
          document.head.appendChild(script)
        } else {
          setTurnstileListeners.call(this);
        }
        window.onloadTurnstileCallback = () => {
          setTurnstileListeners.call(this);
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../../core/scss/variables";

  .login {
    width: 680px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    height: 100%;
    justify-content: center;
  }

  .form-wrapper {
    padding: 50px;
    position: relative;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.5);
    background: -webkit-linear-gradient(left, $primary, $primary);
  }

  .btn-secondary {
    border-radius: 1.5rem;
  }

  .btn-action {
    border-radius: 1.5rem;
    float: right;
    width: 160px;
  }

  .col-right {
    display: flex;
    align-items: center;
  }

  .login-left {
    text-align: center;
    color: #fff;
    margin-top: 4%;

    input {
      border: none;
      border-radius: 1.5rem;
      padding: 2%;
      width: 60%;
      background: #f8f9fa;
      font-weight: bold;
      color: #383d41;
      margin-bottom: 3%;
      cursor: pointer;
    }

    img {
      margin-top: 15%;
      margin-bottom: 5%;
      width: 50%;
    }
  }

  .login-right {
    flex: 1;
    min-height: 320px;
    padding: 30px 50px;
    background: #f8f9fa;
    border-top-left-radius: 10% 50%;
    border-bottom-left-radius: 10% 50%;
  }

  .login-heading {
    text-align: center;
    color: #495057;
  }

  @media (max-width: 767px) {
    .form-wrapper {
      padding: 20px;
    }
    .login-right {
      padding: 20px;
      border-radius: 6px;
    }
  }

  .code-form {
    /deep/ .form-group {
      margin-bottom: 0;
    }
  }

  .login-alert-msg-box {
    font-size: 12px
  }
</style>
